import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BoltIcon,
  ChartBarIcon,
  ClockIcon,
  ShieldCheckIcon,
  ArrowTrendingUpIcon,
  CheckBadgeIcon,
  ArrowRightIcon,
  PlayIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon
} from '@heroicons/react/24/outline';

const LiveTrading = () => {
  const features = [
    {
      icon: BoltIcon,
      title: 'Lightning Fast Execution',
      description: 'Sub-millisecond order execution with advanced order routing for optimal fill prices.'
    },
    {
      icon: ChartBarIcon,
      title: 'Advanced Charting',
      description: 'Professional-grade charts with 100+ technical indicators and drawing tools.'
    },
    {
      icon: ClockIcon,
      title: 'Real-Time Data',
      description: 'Live market data feeds with Level II quotes and time & sales information.'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Risk Management',
      description: 'Built-in risk controls including stop-loss, take-profit, and position sizing tools.'
    },
    {
      icon: DevicePhoneMobileIcon,
      title: 'Mobile Trading',
      description: 'Trade on the go with full-featured mobile apps for iOS and Android.'
    },
    {
      icon: ComputerDesktopIcon,
      title: 'Multi-Platform',
      description: 'Access your account from web, desktop, and mobile with synchronized data.'
    }
  ];

  const tradingTools = [
    {
      title: 'Order Types',
      items: ['Market Orders', 'Limit Orders', 'Stop Orders', 'Trailing Stops', 'Iceberg Orders', 'Time-in-Force Options']
    },
    {
      title: 'Analytics',
      items: ['Real-Time P&L', 'Portfolio Analytics', 'Performance Metrics', 'Trade History', 'Tax Reports', 'Risk Analysis']
    },
    {
      title: 'Market Data',
      items: ['Level II Quotes', 'Time & Sales', 'Market Depth', 'News Feed', 'Economic Calendar', 'Earnings Calendar']
    }
  ];

  const stats = [
    { label: 'Execution Speed', value: '<1ms' },
    { label: 'Uptime', value: '99.99%' },
    { label: 'Markets', value: '150+' },
    { label: 'Assets', value: '10,000+' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 lg:pt-32 lg:pb-24 bg-gradient-to-br from-slate-950 via-slate-900 to-black overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <div className="inline-flex items-center px-4 py-2 bg-amber-500/10 border border-amber-400/20 rounded-full">
                  <span className="text-amber-300 text-sm font-medium">Live Trading</span>
                </div>
                
                <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight">
                  Real-Time Trading
                  <span className="block bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 bg-clip-text text-transparent mt-2">
                    Professional Tools
                  </span>
                </h1>
                
                <p className="text-xl text-gray-300 leading-relaxed">
                  Trade directly on live markets with lightning-fast execution and professional-grade 
                  tools. Experience institutional-quality trading at your fingertips.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-bold text-lg rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-amber-500/50"
                >
                  Start Trading
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
                <button className="inline-flex items-center justify-center px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 hover:border-amber-500/50 text-white font-semibold text-lg rounded-xl transition-all duration-300">
                  <PlayIcon className="mr-2 h-5 w-5" />
                  Watch Demo
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 pt-8 border-t border-gray-800">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl lg:text-3xl font-bold text-amber-400 mb-1">{stat.value}</div>
                    <div className="text-sm text-gray-400">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden border-2 border-amber-500/30 shadow-2xl">
                <img
                  src="/images/new-images/live-trading.jpg"
                  alt="Live Trading Platform"
                  className="w-full h-auto object-cover"
                  onError={(e) => {
                    e.target.src = '/images/new-images/trading-workspace.jpg';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                <div className="absolute top-6 right-6 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">LIVE</div>
                    <div className="text-xs text-gray-300">Trading</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-24 lg:py-32 bg-white overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-500"></div>
              <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-500"></div>
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Professional Trading Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Everything you need for successful live trading
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative bg-white rounded-2xl border-2 border-gray-200 p-8 hover:border-amber-500 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex-shrink-0 w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-amber-200 transition-colors duration-300">
                  <feature.icon className="w-7 h-7 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trading Tools Section */}
      <section className="relative py-24 lg:py-32 bg-black overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-500"></div>
              <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-500"></div>
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              Advanced Trading Tools
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Comprehensive suite of tools for professional traders
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {tradingTools.map((tool, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl border-2 border-amber-500/30 p-8 hover:border-amber-500/50 transition-all duration-300"
              >
                <h3 className="text-2xl font-bold text-white mb-6">{tool.title}</h3>
                <ul className="space-y-4">
                  {tool.items.map((item, idx) => (
                    <li key={idx} className="flex items-center">
                      <CheckBadgeIcon className="h-5 w-5 text-amber-400 mr-3 flex-shrink-0" />
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-br from-amber-50 to-yellow-50 overflow-hidden">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Start Live Trading Today
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Join thousands of traders using our professional live trading platform
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-bold text-lg rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Get Started Free
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/trading"
                className="inline-flex items-center justify-center px-8 py-4 bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-amber-500 text-gray-900 font-semibold text-lg rounded-xl transition-all duration-300"
              >
                Explore Platform
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LiveTrading;

